import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { formatEuros } from "@/lib/format";
import { Prevision } from "./Indicateurs";
import { Bloc, Barre, pct, Vide } from "./commun";

const Alerte = ({ r, testId }) => (
  <Link to={r.lien} className="at-soft p-3 flex gap-3 items-start min-h-[44px]" data-testid={testId}>
    <TriangleAlert size={16} className="shrink-0 mt-0.5" style={{ color: "var(--danger)" }} />
    <span className="min-w-0"><span className="font-semibold block">{r.titre}</span><span className="text-xs at-muted break-words">{r.detail}</span></span>
  </Link>
);

const Partenaires = ({ liste }) => (
  <Bloc titre="Santé des partenaires" sous="Score de réactivité, clients non contactés dans le délai, taux de signature" testId="director-partners">
    {liste.length === 0 && <Vide />}
    <div className="space-y-3">
      {liste.map((p) => (
        <div key={p.id} data-testid={`partner-health-${p.id}`}>
          <div className="flex justify-between gap-2 text-sm"><span className="font-semibold truncate">{p.nom}</span><span className="shrink-0 tabular-nums">{p.score}/100</span></div>
          <Barre pct={p.score} couleur={p.score >= 70 ? "var(--success)" : p.score >= 50 ? "var(--temp-4)" : "var(--danger)"} />
          <p className="text-xs at-muted mt-1">{p.retards} retard(s) · signature {pct(p.taux_signature)} sur {p.devis} devis{p.delai_moyen_h !== null ? ` · contact en ${String(p.delai_moyen_h).replace(".", ",")} h en moyenne` : ""}</p>
        </div>
      ))}
    </div>
  </Bloc>
);

const Commissions = ({ liste }) => (
  <Bloc titre="Commissions par partenaire" testId="director-commissions" action={<Link to="/parametres/commissions" className="text-sm font-semibold min-h-[44px] min-w-[44px] justify-center flex items-center shrink-0" data-testid="director-commissions-link">Détail</Link>}>
    {liste.length === 0 && <Vide texte="Aucune commission pour le moment." />}
    <div className="space-y-2">
      {liste.map((c) => (
        <div key={c.nom} className="at-soft p-3" data-testid={`commission-row-${c.nom}`}>
          <p className="font-semibold text-sm">{c.nom}</p>
          <div className="grid grid-cols-3 gap-2 text-xs mt-1">
            <p>Dû<br /><b className="text-sm">{formatEuros(c.du)}</b></p><p>Facturé<br /><b className="text-sm">{formatEuros(c.facture)}</b></p><p>Encaissé<br /><b className="text-sm">{formatEuros(c.encaisse)}</b></p>
          </div>
        </div>
      ))}
    </div>
  </Bloc>
);

export const AccueilDirigeant = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/tableaux/dirigeant").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-4 mb-6 items-start" data-testid="director-home">
      <Prevision p={d.prevision} testId="director-forecast" />
      <Bloc titre="Les 3 alertes les plus urgentes" testId="director-alerts">
        {d.alertes.length === 0 ? <Vide texte="Aucune alerte urgente." testId="director-alerts-empty" /> : <div className="space-y-2">{d.alertes.map((r, i) => <Alerte key={i} r={r} testId={`director-alert-${i}`} />)}</div>}
      </Bloc>
      <Partenaires liste={d.partenaires} />
      <Commissions liste={d.commissions} />
      <div className="lg:col-span-2 min-w-0">
        <Bloc titre="Autres recommandations et urgences du jour" sous="Fiches en retard, devis sans relance, partenaires non réactifs, agents en baisse" testId="director-recommendations">
          {d.recommandations.length === 0 ? <Vide texte="Rien d'autre à signaler aujourd'hui." testId="director-recommendations-empty" /> : <div className="grid md:grid-cols-2 gap-2">{d.recommandations.map((r, i) => <Alerte key={i} r={r} testId={`director-reco-${i}`} />)}</div>}
          <p className="text-xs at-muted mt-3 flex items-center gap-1.5" data-testid="director-voice-hint"><Mic size={14} />Posez vos questions à voix haute avec le micro jaune : « quelle est la prévision de fin de mois ? », « combien de devis signés ce mois ? ».</p>
        </Bloc>
      </div>
    </div>
  );
};
